<template>
    <!-- 触发器：展示当前颜色；点击弹出取色面板 -->
    <b-dropdown ref="dd" variant="light" size="sm" no-caret
                toggle-class="tcp-toggle" menu-class="tcp-menu">
        <template #button-content>
            <span class="tcp-trigger" :style="{ backgroundColor: triggerColor }"></span>
            <span class="tcp-trigger-label">{{ triggerLabel }}</span>
            <i class="fa-solid fa-caret-down ml-1 tcp-caret"></i>
        </template>

        <div class="tcp-panel">
            <!-- 颜色选择器：自定义 hex（实时变化） -->
            <div class="tcp-head">
                <input type="color" class="tcp-native" :class="{ 'is-active': isHex }"
                       :value="hexValue" :title="isHex ? value : '自定义颜色'"
                       @input="onInput($event.target.value)"
                       @change="onChange($event.target.value)">
                <span class="tcp-head__hint">自定义颜色</span>
                <span v-if="value" class="tcp-clear" title="清除" role="button"
                      @click="select('')">
                    <i class="fa-solid fa-xmark"></i>
                </span>
            </div>

            <!-- 预设颜色：弹窗内、选择器下方 -->
            <div class="tcp-section">
                <div class="tcp-section__title">预设</div>
                <div class="tcp-swatches">
                    <span v-for="t of tokens" :key="t"
                          class="tcp-swatch" :class="{ 'is-active': value === t }"
                          :style="{ backgroundColor: dotOf(t) }"
                          :title="t" role="button"
                          @click="select(t)"></span>
                </div>
            </div>

            <!-- 最近使用的自定义颜色（localStorage，最多 6 个） -->
            <div v-if="recentColors.length" class="tcp-section">
                <div class="tcp-section__title">最近</div>
                <div class="tcp-swatches">
                    <span v-for="c of recentColors" :key="c"
                          class="tcp-swatch tcp-swatch--square"
                          :class="{ 'is-active': sameHex(value, c) }"
                          :style="{ backgroundColor: c }"
                          :title="c" role="button"
                          @click="select(c)"></span>
                </div>
            </div>
        </div>
    </b-dropdown>
</template>

<script>
import Vue from "vue";
import {resolveTagColor, TAG_COLOR_TOKENS} from "@/components/common/mini-table/cells/selectTagColor";

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const LS_KEY = 'tag_color_recent_colors';
const MAX_RECENT = 6;

function loadRecent() {
    try {
        let raw = localStorage.getItem(LS_KEY);
        let arr = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(arr)) {
            return [];
        }
        return arr.filter(x => typeof x === 'string' && HEX_RE.test(x)).slice(0, MAX_RECENT);
    } catch (e) {
        return [];
    }
}

function saveRecent(list) {
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(list));
    } catch (e) {
        // ignore quota / privacy-mode errors
    }
}

// 跨实例共享的响应式最近颜色列表（同一会话内多处取色保持同步）
const recentStore = Vue.observable({colors: loadRecent()});

function normHex(hex) {
    let v = ('' + hex).trim().toLowerCase();
    if (!HEX_RE.test(v)) {
        return null;
    }
    if (v.length === 4) {
        v = '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
    }
    return v;
}

export default {
    name: "TagColorPicker",
    props: {
        value: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            tokens: TAG_COLOR_TOKENS,
        }
    },
    computed: {
        recentColors() {
            return recentStore.colors;
        },
        isHex() {
            return HEX_RE.test((this.value || "").trim());
        },
        // 原生 input[type=color] 仅接受 6 位 #rrggbb；
        // 无自定义色时回退到预设默认色（neutral）
        hexValue() {
            return normHex(this.value) || resolveTagColor('').dot;
        },
        triggerColor() {
            return resolveTagColor(this.value).dot;
        },
        triggerLabel() {
            return this.value || '默认';
        }
    },
    methods: {
        dotOf(token) {
            return resolveTagColor(token).dot;
        },
        sameHex(a, b) {
            let na = normHex(a), nb = normHex(b);
            return !!na && na === nb;
        },
        select(color) {
            this.$emit('input', color);
        },
        // 拖动时实时回传，标签即时变化（不写入最近）
        onInput(color) {
            this.select(color);
        },
        // 确认后写入最近颜色
        onChange(color) {
            this.select(color);
            this.addRecent(color);
        },
        addRecent(hex) {
            let v = normHex(hex);
            if (!v) {
                return;
            }
            let list = recentStore.colors.filter(c => c.toLowerCase() !== v);
            list.unshift(v);
            if (list.length > MAX_RECENT) {
                list = list.slice(0, MAX_RECENT);
            }
            recentStore.colors = list;
            saveRecent(list);
        }
    }
}
</script>

<style scoped>
.tcp-trigger {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 4px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, .08) inset;
    vertical-align: middle;
}

.tcp-trigger-label {
    margin-left: 6px;
    font-size: 12px;
    vertical-align: middle;
}

.tcp-caret {
    font-size: 10px;
    color: #94a3b8;
}

.tcp-menu {
    padding: 0;
}

.tcp-panel {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    min-width: 220px;
}

.tcp-head {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.tcp-head__hint {
    font-size: 12px;
    color: #94a3b8;
}

.tcp-native {
    width: 26px;
    height: 26px;
    padding: 0;
    border: 2px solid #e3e9f0;
    border-radius: 6px;
    cursor: pointer;
    background: none;
}

.tcp-native.is-active {
    border-color: #1e5274;
}

.tcp-native::-webkit-color-swatch-wrapper {
    padding: 0;
}

.tcp-native::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
}

.tcp-section {
    display: flex;
    align-items: center;
    gap: 8px;
}

.tcp-section__title {
    font-size: 11px;
    color: #94a3b8;
    width: 28px;
    flex: 0 0 auto;
}

.tcp-swatches {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.tcp-swatch {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid transparent;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, .08) inset;
}

.tcp-swatch--square {
    border-radius: 5px;
}

.tcp-swatch.is-active {
    border-color: #1e5274;
    box-shadow: 0 0 0 2px rgba(30, 82, 116, .25);
}

.tcp-clear {
    cursor: pointer;
    color: #94a3b8;
    font-size: 12px;
    line-height: 1;
    margin-left: auto;
}

.tcp-clear:hover {
    color: #dc2626;
}
</style>
